import React from 'react';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCmsUrlValue, getCmsValue } from '../lib/siteContent';
import OpeningHours from './OpeningHours';

const isHttpsUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

const isCmsEnabled = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'ja', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'nej', 'off'].includes(normalized)) return false;
  return fallback;
};

const formatAddress = (street, postalCode, locality) => [
  street,
  [postalCode, locality].filter(Boolean).join(' '),
].filter(Boolean).join(', ');

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M15 4v10.5a4.5 4.5 0 1 1-4-4.47" />
      <path d="M15 4c.7 2.1 2.2 3.6 4 4" />
    </svg>
  );
}

/**
 * Global footer för de publika smokehouse-sidorna. Redaktionella rubriker ligger
 * i global_footer, medan kontakt-, plats- och sociala fakta har en enda källa i
 * business_identity. Tomma eller ogiltiga fakta visas inte.
 */
export default function SmokeFooter({ siteContent = {} }) {
  const footer = (field) => getCmsValue(siteContent, 'global_footer', field);
  const identity = (field) => getCmsValue(siteContent, 'business_identity', field);
  const nav = (field) => getCmsValue(siteContent, 'global_nav', field);
  const navUrl = (field) => getCmsUrlValue(siteContent, 'global_nav', field);

  const phone = identity('phone');
  const email = identity('email');
  const productsVisible = isCmsEnabled(nav('products_visible'), true);
  const orderVisible = isCmsEnabled(nav('order_visible'), true);
  const smokehouseAddress = formatAddress(
    identity('smokehouse_street_address'),
    identity('smokehouse_postal_code'),
    identity('smokehouse_locality')
  );
  const vassenAddress = formatAddress(
    identity('vassen_street_address'),
    identity('vassen_postal_code'),
    identity('vassen_locality')
  );
  const places = [
    {
      name: identity('smokehouse_name'),
      description: identity('smokehouse_description'),
      address: smokehouseAddress,
    },
    {
      name: identity('vassen_name'),
      description: identity('vassen_description'),
      address: vassenAddress,
    },
  ].filter((place) => place.name || place.description || place.address);
  const socials = [
    { label: 'Instagram', url: identity('instagram_url'), Icon: Instagram },
    { label: 'Facebook', url: identity('facebook_url'), Icon: Facebook },
    { label: 'TikTok', url: identity('tiktok_url'), Icon: TikTokIcon },
    { label: 'YouTube', url: identity('youtube_url'), Icon: Youtube },
  ].filter((item) => isHttpsUrl(item.url));
  const hasContact = phone || email || places.length > 0;

  return (
    <footer className="smoke-footer">
      <div className="smoke-wrap">
        <div className="smoke-footer-grid">
          <div className="smoke-footer-brand">
            <img src="/assets/smokehouse/harryda-bbq-logo-400.webp" alt="" aria-hidden="true" />
            <strong>{nav('brand_label')}</strong>
            {footer('tagline') && <p>{footer('tagline')}</p>}
          </div>
          <div>
            {hasContact && <span className="smoke-kicker">{footer('contact_title')}</span>}
            <ul>
              {phone && <li><a href={telHref(phone)}>{phone}</a></li>}
              {email && <li><a href={`mailto:${email}`}>{email}</a></li>}
              {places.map((place) => (
                <li className="smoke-footer-place" key={`${place.name}-${place.address}`}>
                  {place.name && <strong className="smoke-footer-place-name">{place.name}</strong>}
                  {place.description && <span className="smoke-footer-place-description">{place.description}</span>}
                  {place.address && <address className="smoke-footer-place-address">{place.address}</address>}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="smoke-kicker">{footer('links_title')}</span>
            <ul>
              <li><Link to={navUrl('home_url')}>{nav('home_label')}</Link></li>
              <li><Link to={navUrl('catering_url')}>{nav('catering_label')}</Link></li>
              {productsVisible && <li><Link to={navUrl('products_url')}>{nav('products_label')}</Link></li>}
              {nav('pulled_pork_label') && navUrl('pulled_pork_url') && <li><Link to={navUrl('pulled_pork_url')}>{nav('pulled_pork_label')}</Link></li>}
              {nav('brisket_label') && navUrl('brisket_url') && <li><Link to={navUrl('brisket_url')}>{nav('brisket_label')}</Link></li>}
              {orderVisible && <li><Link to={navUrl('order_url')}>{nav('order_label')}</Link></li>}
              <li><a href={navUrl('values_url')}>{nav('values_label')}</a></li>
              <li><Link to={navUrl('contact_url')}>{nav('contact_label')}</Link></li>
            </ul>
          </div>
          <OpeningHours />
          <div>
            {socials.length > 0 && (
              <>
                <span className="smoke-kicker">{footer('social_title')}</span>
                <ul>
                  {socials.map(({ label, url, Icon }) => (
                    <li key={label}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <Icon size={18} aria-hidden="true" focusable="false" />
                        <span>{label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
        <div className="smoke-footer-bottom">
          <span>© {new Date().getFullYear()} {nav('brand_label')}</span>
          {footer('org_text') && <span>{footer('org_text')}</span>}
        </div>
      </div>
    </footer>
  );
}
